  /** @jsx jsx */
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { jsx } from '@emotion/core';
import { Label } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import DateIconButton from '../../EnhancedDataTable/EnhancedTableFilter/DateFilter/DateIconButton';
import '../../../styles/react-date-time-picker.css';

export const getStrFormattedDateTime = (date) => {
  if (date instanceof Date) {
    const dateStr = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
    const timeStr = `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
    return `${dateStr} ${timeStr}`;
  }
  return date.toString();
};
export const isSameDay = (date1, date2) => {
  if (date1 instanceof Date && date2 instanceof Date) {
    const d1 = new Date(date1);
    d1.setHours(0, 0, 0, 0);
    const d2 = new Date(date2);
    d2.setHours(0, 0, 0, 0);
    return Number(d1) === Number(d2);
  }
  return false;
};
function getDefaultInfo(intervalInDays) {
  const tempMinDate = new Date();
  const tempMaxDate = new Date();
  tempMinDate.setDate(tempMinDate.getDate() - 365);
  tempMaxDate.setDate(tempMinDate.getDate() + 1);
  tempMinDate.setHours(0, 0, 0);
  tempMaxDate.setHours(23, 59, 59);

  const startDateTime = new Date();
  startDateTime.setDate(startDateTime.getDate() - intervalInDays);

  return {
    minStartDateTime: tempMinDate,
    maxStartDateTime: tempMaxDate,
    minEndDateTime: new Date(new Date().setHours(0, 0, 0)),
    maxEndDateTime: tempMaxDate,
    minStartTime: tempMinDate,
    maxStartTime: tempMaxDate,
    minEndTime: new Date(new Date().setHours(0, 0, 0)),
    maxEndTime: tempMaxDate,
    startDateTime,
    endDateTime: new Date(new Date()),
  };
}
function initializeData(val, intervalInDays = 15) {
  const defaultDateTime = getDefaultInfo(intervalInDays);
  if (val) {
    const dateTimeArr = val.split('-');
    if (dateTimeArr.length > 1) {
      const startDateTime = dateTimeArr[0];
      const endDateTime = dateTimeArr[1];
      defaultDateTime.startDateTime = new Date(startDateTime);
      defaultDateTime.endDateTime = new Date(endDateTime);
      defaultDateTime.maxStartDateTime = new Date(endDateTime);
      defaultDateTime.minEndDateTime = new Date(startDateTime);
      if (isSameDay(defaultDateTime.startDateTime, defaultDateTime.endDateTime)) {
        defaultDateTime.maxStartTime = defaultDateTime.endDateTime;
        defaultDateTime.minEndTime = defaultDateTime.startDateTime;
      }
    }
  }
  return defaultDateTime;
}
const AdvFormDateTimeRangeSelectItem = React.memo(({
  formField, pageData, setPageData,
}) => {
  const startDateTimeType = 'start_dateTime';
  const endDateTimeType = 'end_dateTime';
  const startDateTimeId = `${formField.id}-${startDateTimeType}`;
  const endDateTimeId = `${formField.id}-${endDateTimeType}`;

  const getLabel = (fieldData, type) => {
    const labelArr = fieldData.label.split('-');
    let label = labelArr[0].trim();
    if (type === endDateTimeType) {
      label = labelArr.length > 1 ? labelArr[1].trim() : label;
    }
    return (
      <Label id={`advFormSearch-lbl-${fieldData.name}-${endDateTimeType}`} htmlFor={`${fieldData.name}`}>
        {label}
      </Label>
    );
  };

  const [info, setInfo] = React.useState(initializeData(formField.value, formField.intervalInDays));

  useEffect(() => {
    setInfo(initializeData(formField.value, formField.intervalInDays));
  }, [pageData]);

  return (
    <>
      <div key={startDateTimeId} id={startDateTimeId} className="col-md-3">
        {getLabel(formField, startDateTimeType)}
        <DateTimePickerComponent
          formField={formField}
          info={info}
          setInfo={setInfo}
          type={startDateTimeType}
          pageData={pageData}
          setPageData={setPageData}
        />
      </div>
      <div key={endDateTimeId} id={endDateTimeId} className="col-md-3">
        {getLabel(formField, endDateTimeType)}
        <DateTimePickerComponent
          formField={formField}
          info={info}
          setInfo={setInfo}
          type={endDateTimeType}
          pageData={pageData}
          setPageData={setPageData}
        />
      </div>
    </>

  );
});
const DateTimePickerComponent = ({
  formField, info, setInfo, type, pageData, setPageData,
}) => {
  const { id } = formField;

  const [calendarIsOpen, setCalendarIsOpen] = useState(false);

  const dateTime = type === 'start_dateTime' ? info.startDateTime : info.endDateTime;

  const minDateTime = type === 'start_dateTime' ? info.minStartDateTime : info.minEndDateTime;
  const maxDateTime = type === 'start_dateTime' ? info.maxStartDateTime : info.maxEndDateTime;
  const minTime = type === 'start_dateTime' ? info.minStartTime : info.minEndTime;
  const maxTime = type === 'start_dateTime' ? info.maxStartTime : info.maxEndTime;

  const idInput = `${formField.name}-${formField.type}`;
  const idButton = `${formField.name}-button-${formField.type}`;

  const handleOnChange = (val) => {
    const updatedPageData = { ...pageData };
    if (type.includes('start_dateTime')) {
      updatedPageData.formFields[id].value = `${getStrFormattedDateTime(val)}-${getStrFormattedDateTime(info.endDateTime)}`;
    } else if (type.includes('end_dateTime')) {
      updatedPageData.formFields[id].value = `${getStrFormattedDateTime(info.startDateTime)}-${getStrFormattedDateTime(val)}`;
    }
    const updatedInfodata = initializeData(updatedPageData.formFields[id].value, formField.intervalInDays);
    setPageData(updatedPageData);
    setInfo(updatedInfodata);
    setCalendarIsOpen(false);
  };

  useEffect(() => {
    if (!formField.value) {
      const updatedPageData = { ...pageData };
      updatedPageData.formFields[id].value = `${getStrFormattedDateTime(info.startDateTime)}-${getStrFormattedDateTime(info.endDateTime)}`;
      setPageData(updatedPageData);
    }
  }, [info]);

  function clickOutside(ref, calendarRef) {
    function handleClickOutside(event) {
      if (calendarIsOpen) {
        if (
          ref.current
          && !ref.current.contains(event.target)
          && calendarRef
          && !calendarRef.current.contains(event.target)
        ) {
          setCalendarIsOpen(!calendarIsOpen);
        }
      }
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    });
  }

  const wrapperRef = useRef(null);
  const calendarRef = useRef(null);
  clickOutside(wrapperRef, calendarRef);

  return (
    <div ref={calendarRef} className="react-datepicker-wrapper-outer">
      <DatePicker
        id={idInput}
        selected={dateTime}
        onChange={handleOnChange}
        showTimeSelect={true}
        timeFormat="HH:mm"
        timeIntervals={15}
        timeCaption="time"
        dateFormat="yyyy/MM/dd h:mm aa"
        minDate={minDateTime}
        maxDate={maxDateTime}
        minTime={minTime}
        maxTime={maxTime}
        open={calendarIsOpen}
        ref={(r) => r && r.setOpen(false)}
      />
      <DateIconButton
        label="temp1"
        iconFilled={false}
        id={idButton}
        isDisable={false}
        onClick={() => setCalendarIsOpen(!calendarIsOpen)}
        ref={wrapperRef}
      />
    </div>
  );
};
DateTimePickerComponent.propTypes = {
  formField: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    value: PropTypes.string.isRequired,
    intervalInDays: PropTypes.number,
  }),
  info: PropTypes.shape({
    startDateTime: PropTypes.instanceOf(Date),
    endDateTime: PropTypes.instanceOf(Date),
    minStartDateTime: PropTypes.instanceOf(Date),
    minEndDateTime: PropTypes.instanceOf(Date),
    maxStartDateTime: PropTypes.instanceOf(Date),
    maxEndDateTime: PropTypes.instanceOf(Date),
    minStartTime: PropTypes.instanceOf(Date),
    minEndTime: PropTypes.instanceOf(Date),
    maxStartTime: PropTypes.instanceOf(Date),
    maxEndTime: PropTypes.instanceOf(Date),
  }),
  setInfo: PropTypes.func.isRequired,
  type: PropTypes.string,
  formFields: PropTypes.shape({
    value: PropTypes.string.isRequired,
  }),
  pageData: PropTypes.shape({
    formHeader: PropTypes.string,
    formFields: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
      })
    ),
  }),
  setPageData: PropTypes.func.isRequired,
};
export default AdvFormDateTimeRangeSelectItem;
